"use client";

import { PlannerSession } from "@/hooks/client/usePlannerChat";
import { AlertTriangle, MessageSquarePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface PlannerSidebarProps {
  sessions: PlannerSession[];
  sessionsLoading: boolean;
  sessionsError?: boolean;
  currentSessionId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectSession: (id: string) => void;
  onNewChat: () => void;
  onDeleteSession: (id: string) => void;
  onRetryLoadSessions?: () => void;
}

export default function PlannerSidebar({
  sessions,
  sessionsLoading,
  sessionsError,
  currentSessionId,
  isOpen,
  onClose,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  onRetryLoadSessions,
}: PlannerSidebarProps) {
  const newChatButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={onNewChat}
      className="w-full justify-start gap-2"
    >
      <MessageSquarePlus className="h-4 w-4" />
      New conversation
    </Button>
  );

  const sessionList = sessionsLoading ? (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  ) : sessionsError && sessions.length === 0 ? (
    <div className="flex flex-col items-center gap-2 px-3 py-4 text-center">
      <AlertTriangle className="h-4 w-4 text-destructive" />
      <p className="text-xs text-muted-foreground">
        Couldn&apos;t load past conversations.
      </p>
      {onRetryLoadSessions && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRetryLoadSessions}
          className="text-xs"
        >
          Retry
        </Button>
      )}
    </div>
  ) : sessions.length === 0 ? (
    <p className="px-3 py-4 text-center text-xs text-muted-foreground">
      No past conversations yet.
    </p>
  ) : (
    <div className="space-y-1">
      {sessions.map((session) => (
        <div
          key={session.id}
          className={`group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
            currentSessionId === session.id
              ? "bg-primary/10 text-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          <Button
            variant="link"
            size="none"
            onClick={() => onSelectSession(session.id)}
            className="flex-1 justify-start truncate text-left text-sm"
          >
            {session.name || "Untitled conversation"}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onDeleteSession(session.id);
            }}
            className="hidden h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive group-hover:block"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar (always visible on md+) */}
      <div className="hidden w-64 shrink-0 flex-col border-r border-border md:flex">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-sm font-semibold text-foreground">
            Past Conversations
          </h2>
        </div>
        <div className="p-3">{newChatButton}</div>
        <div className="flex-1 overflow-y-auto px-3">{sessionList}</div>
      </div>

      {/* Mobile conversation history uses a bottom sheet, matching the app.
          modal={false} disables Radix's mount-based scroll-lock + focus-trap
          so resizing from mobile to desktop doesn't leave the page locked. */}
      <Sheet modal={false} open={isOpen} onOpenChange={(v) => !v && onClose()}>
        <SheetContent
          side="bottom"
          size="lg"
          className="md:hidden"
          overlayClassName="md:hidden"
        >
          <SheetHeader>
            <SheetTitle>Past Conversations</SheetTitle>
          </SheetHeader>
          <div className="py-3">{newChatButton}</div>
          <SheetBody>{sessionList}</SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
